// src/components/verificador.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MessageSquare, Search, X, CheckCircle, Clock } from 'lucide-react';

const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-raffle-app';

const AGENT_NAMES = ['Ana', 'Carlos', 'Sof\u00eda', 'Diego', 'Valentina', 'Luis', 'Mar\u00eda', 'Juan', 'Fernanda', 'Miguel'];

export default function Verificador() {
  const [open, setOpen] = useState(false);
  const [agentName, setAgentName] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) {
      const name = AGENT_NAMES[Math.floor(Math.random() * AGENT_NAMES.length)];
      setAgentName(name);
      setMessages([
        { id: 's-1', sender: 'system', text: 'Conectando al verificador...' },
      ]);

      const t1 = setTimeout(() => {
        setMessages(prev => ([...prev,
          { id: 'bot-hello', sender: 'bot', text: `Te est\u00e1 atendiendo: ${name}` },
          { id: 'bot-welcome', sender: 'bot', text: 'Por favor ingresa tu número de teléfono (10 dígitos) para localizar tus boletos.' }
        ]));
      }, 700);

      return () => clearTimeout(t1);
    } else {
      setMessages([]);
      setAgentName(null);
      setInput('');
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const pushMessage = (msg) => setMessages(m => [...m, { id: `${Date.now()}-${Math.random()}`, ...msg }]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const raw = input.trim();
    if (!raw) return;

    // Push user's message (as entered)
    pushMessage({ sender: 'user', text: raw });
    setInput('');

    // Validate phone: accept only digits, require 10 digits
    const phoneDigits = raw.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      pushMessage({ sender: 'bot', text: 'Por favor ingresa un número de teléfono válido de 10 dígitos.' });
      return;
    }

    const loadingId = `bot-loading-${Date.now()}`;
    setLoading(true);
    pushMessage({ id: loadingId, sender: 'bot', text: 'Buscando en la base de datos...' });

    try {
      const reservationsCol = collection(db, `artifacts/${appId}/public/data/reservations`);

      let results = [];
      // Buscar exclusivamente por el campo phone
      try {
        // Try exact match with the digits-only phone first
        const qPhoneDigits = query(reservationsCol, where('phone', '==', phoneDigits));
        const snapDigits = await getDocs(qPhoneDigits);
        if (!snapDigits.empty) snapDigits.forEach(d => results.push({ id: d.id, ...d.data() }));

        // If nothing, also try the raw input (in case phone stored with country code or formatting)
        if (results.length === 0) {
          const qPhoneRaw = query(reservationsCol, where('phone', '==', raw));
          const snapRaw = await getDocs(qPhoneRaw);
          if (!snapRaw.empty) snapRaw.forEach(d => results.push({ id: d.id, ...d.data() }));
        }
      } catch (err) {
        console.warn('Error buscando por phone:', err);
      }

      // Remover loading
      setMessages(prev => prev.filter(m => m.id !== loadingId));

      if (results.length === 0) {
        pushMessage({ sender: 'bot', text: 'No se encontraron reservaciones con ese número. Verifica que lo escribiste correctamente (solo 10 dígitos).' });
      } else {
        results.forEach(res => {
          const status = (res.status || 'confirmed').toLowerCase();
          // Mostrar info básica
          pushMessage({ sender: 'bot', text: `Resultado: ${res.name || 'Sin nombre'} — Boletos: ${Array.isArray(res.tickets) ? res.tickets.join(', ') : 'N/A'}` });
          if (status === 'liquidado') {
            pushMessage({ sender: 'bot', text: '¡Boletos Liquidados!', meta: { type: 'liquidado' } });
            pushMessage({ sender: 'bot', text: 'Felicidades, ya estás participando oficialmente en el sorteo.' });
          } else {
            pushMessage({ sender: 'bot', text: 'Verificación de pago: Estatus Pendiente', meta: { type: 'pending' } });
            pushMessage({ sender: 'bot', text: 'Si ya enviaste tu comprobante, no te preocupes. Ya estás participando, nuestros administradores están validando tu pago.' });
          }
        });
      }

    } catch (error) {
      console.error('Error en la verificación:', error);
      setMessages(prev => prev.filter(m => !m.id.toString().startsWith('bot-loading-')));
      pushMessage({ sender: 'bot', text: 'Ocurrió un error al consultar la base de datos. Intenta de nuevo más tarde.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50">
        {!open && (
          <button onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-3 ring-1 ring-indigo-300">
            <MessageSquare size={20} />
            <span className="font-semibold text-sm">Verificar Boleto</span>
          </button>
        )}

        {open && (
          <div className="w-80 md:w-96 h-96 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-sm font-semibold">{agentName ? agentName[0] : '?'}</div>
                <div>
                  <div className="text-sm font-semibold">{agentName ? `Asistente: ${agentName}` : 'Asistente'}</div>
                  <div className="text-xs opacity-80">En línea</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setOpen(false); setMessages([]); }} className="p-1 rounded hover:bg-white/10"><X size={18} className="text-white" /></button>
              </div>
            </div>

            <div className="flex-1 p-3 overflow-y-auto bg-gray-50">
              <div className="space-y-3">
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`${msg.sender === 'user' ? 'bg-green-600 text-white' : 'bg-white text-gray-800'} p-2 rounded-lg shadow-sm max-w-[86%]`}>
                      {msg.meta && msg.meta.type === 'liquidado' && (
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="text-green-500" />
                          <span className="font-semibold text-sm">¡Boletos Liquidados!</span>
                        </div>
                      )}
                      {msg.meta && msg.meta.type === 'pending' && (
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="text-yellow-500" />
                          <span className="font-semibold text-sm">Estatus Pendiente</span>
                        </div>
                      )}

                      <div className="text-sm">{msg.text}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-3 border-t bg-white flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ingresa tu teléfono (10 dígitos)" className="flex-1 border rounded-full px-3 py-2 text-sm focus:outline-none" />
              <button type="submit" disabled={loading} className="bg-blue-600 text-white p-2 rounded-full flex items-center justify-center disabled:opacity-60">
                <Search size={16} />
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}

